import { useEffect, useRef } from 'react'
import { Sparkles } from 'lucide-react'
import type { ChatMessage } from '@/lib/types'
import { UserBubble } from './UserBubble'
import { AssistantBubble } from './AssistantBubble'

interface Props {
  messages: ChatMessage[]
  onInsertToInput: (md: string) => void
}

/**
 * Scrollable chat history. Auto-scrolls to bottom whenever a new message
 * lands (typical chat behavior). Empty state shows a friendly hint.
 */
export function ChatLog({ messages, onInsertToInput }: Props) {
  const bottomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' })
  }, [messages])

  if (messages.length === 0) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 py-8 text-center">
        <div className="mb-3 rounded-full bg-blue-50 p-3 ring-1 ring-blue-100">
          <Sparkles className="h-5 w-5 text-blue-600" />
        </div>
        <h2 className="text-[14px] font-semibold text-zinc-900">
          Type a note to get started
        </h2>
        <p className="mt-1 max-w-[260px] text-[12px] leading-relaxed text-zinc-500">
          Drop notes about the call as it happens. After each message, AI suggestions
          appear here based on everything you've typed so far.
        </p>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 px-3 py-3">
      {messages.map((m) =>
        m.kind === 'user' ? (
          <UserBubble key={m.id} msg={m} />
        ) : (
          <AssistantBubble key={m.id} msg={m} onInsertToInput={onInsertToInput} />
        ),
      )}
      <div ref={bottomRef} />
    </div>
  )
}
